import { h, Component } from 'preact';
import Store from 'store/store';

import { joinClasses } from 'utils/utils';
import { ilerp } from 'utils/math';
import { drawCard, feedSize } from 'utils/poster';

import { PinchGesture, RotateGesture, Plus, Minus, RotateLeft, RotateRight } from 'components/bolt/icons/quizPhotoboothIcons';
import HTML from 'components/core/html/html';

import s from 'components/bolt/quizPhotobooth/photoEdit.sss';

export default class PhotoEdit extends Component {
	constructor(props) {
		super(props);
		this.drawHeadshot = this.drawHeadshot.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.onZoomInClick = this.onZoomInClick.bind(this);
		this.onZoomOutClick = this.onZoomOutClick.bind(this);
		this.onCounterRotateClick = this.onCounterRotateClick.bind(this);
		this.onClockwiseRotateClick = this.onClockwiseRotateClick.bind(this);
		this.onRetake = this.onRetake.bind(this);
		this.onConfirm = this.onConfirm.bind(this);
		this.canvasRef = this.canvasRef.bind(this);
		this.imageRoot = this.props.appData.urls.roots.image;

		// For easy overrides
		this.canvasSize = { width: 979, height: 1222 };

		this.state = {
			showTouchInstructions: true,
		};

		if (typeof window !== 'undefined') {
			this.setUpTouchValues();
		}
	}

	componentDidMount() {
		document.addEventListener('pointermove', this.handlePointerMove);
		document.addEventListener('pointerup', this.handlePointerUp);
		document.addEventListener('pointercancel', this.handlePointerUp);
		if (this.props.showEditScreen) {
			this.setUpTouchValues();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		cancelAnimationFrame(this.timer);
		document.removeEventListener('pointermove', this.handlePointerMove);
		document.removeEventListener('pointerup', this.handlePointerUp);
		document.removeEventListener('pointercancel', this.handlePointerUp);
	}

	componentDidUpdate(prevProps) {
		if (this.props.showEditScreen && !prevProps.showEditScreen) {
			this.setUpTouchValues();
		}
		if (this.cardCtx) drawCard(this.props.cardData, this.cardCtx, 'feed');
	}

	setUpTouchValues() {
		const cardData = { ...this.props.cardData };
		const side = cardData.sides[this.props.selectedSide];
		this.transform = side?.photoTransform || new DOMMatrix().translate(0, -489.5);
		this.touches = new Map();
		if (side && !side.photoTransform) {
			side.photoTransform = this.transform;
			this.props.updateCardData(cardData);
		}
	}

	processTouch({ pointerId = 'default', clientX, clientY }) {
		const rect = this.$canvasContainer.getBoundingClientRect();
		const w = this.$canvas.width;
		const h = this.$canvas.height;
		return {
			identifier: pointerId,
			x: (ilerp(clientX, rect.left, rect.right) - .5) * w,
			y: (ilerp(clientY, rect.top, rect.bottom) - .5) * h,
		};
	}
	updateTouch(touch) {
		this.touches.set(touch.identifier, touch);
	}
	clearTouch(touch) {
		return this.touches.delete(touch.identifier);
	}

	handlePointerDown(e) {
		if (e.pointerType === 'mouse' && e.button !== 0) {
			return;
		}
		e.preventDefault();
		this.updateTouch(this.processTouch(e));
	}

	handlePointerMove(e) {
		const touch = this.processTouch(e);
		if (!this.touches.has(touch.identifier)) {
			return;
		}
		e.preventDefault();
		this.updateTransform(touch, ...[...this.touches.values()].filter(t => t.identifier !== touch.identifier));
		this.updateTouch(touch);
	}

	handlePointerUp(e) {
		const hadTouch = this.clearTouch(this.processTouch(e));
		if (hadTouch) {
			Store.emit('TRACK_QUIZPHOTOBOOTH_PHOTO_REPOSITIONED');
		}
	}

	updateTransform(touch1, touch2) {
		const touch1From = this.touches.get(touch1?.identifier);
		if (!touch1From) return;

		if (!touch2) {
			// Single touch, simple translation
			this.transform.preMultiplySelf(new DOMMatrix().translateSelf(touch1.x - touch1From.x, touch1.y - touch1From.y));
			this.drawHeadshot();
			return;
		}

		const touch2From = this.touches.get(touch2.identifier);
		if (!touch2From) return;

		// Double-touch
		// Multiple ways to do this but here we're basically following this answer: https://math.stackexchange.com/a/2790865

		const { x: sCos, y: sSin, z: tx, w: ty } = new DOMMatrix([
			touch1From.x, touch1From.y, touch2From.x, touch2From.y,
			-touch1From.y, touch1From.x, -touch2From.y, touch2From.x,
			1, 0, 1, 0,
			0, 1, 0, 1,
		])
			.invertSelf()
			.transformPoint(new DOMPoint(touch1.x, touch1.y, touch2.x, touch2.y));

		// If the matrix couldn't be inverted, all the values are NaN (just do nothing in this case)
		if (isNaN(sCos)) {
			return;
		}

		this.transform.preMultiplySelf(new DOMMatrix([sCos, sSin, -sSin, sCos, tx, ty]));
		this.drawHeadshot();
	}

	onRetake() {
		this.setUpTouchValues();
		// Store.emit('TRACK_QUIZPHOTOBOOTH_RETAKE_OR_CONTINUE', 'retake-photo');
		this.props.retakePhoto();
		Store.emit('CUSTOM_EVENT', {
			category: 'resize',
			action: 'resize-back-click',
			label: ''
		});
	}

	onConfirm() {
		const { confirm } = this.props;
		cancelAnimationFrame(this.timer);

		// Copy of photo for later use (avoid preact destroying it)
		const photo = document.createElement('canvas');
		photo.width = this.$canvas.width;
		photo.height = this.$canvas.height;
		photo.getContext('2d').drawImage(this.$canvas, 0, 0);
		Store.get().quizPhotobooth.set({ photo });

		Store.emit('TRACK_QUIZPHOTOBOOTH_RETAKE_OR_CONTINUE', 'continue');
		confirm();
		// Store.emit('TRACK_QUIZPHOTOBOOTH_QUIZ_VIEW', 'shareable');
	}

	centeredZoom(factor) {
		// const drawPoint = this.getDrawPoint();
		this.transform.preMultiplySelf(
			new DOMMatrix().scaleSelf(
				factor, factor, undefined,
				0, 0, 1
			)
		);
		this.drawHeadshot();
	}

	onZoomInClick() {
		this.centeredZoom(1.1);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ZOOM_CLICK', 'plus');
	}

	onZoomOutClick() {
		this.centeredZoom(1 / 1.1);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ZOOM_CLICK', 'minus');
	}

	centeredRotate(angleDegrees) {
		// const drawPoint = this.getDrawPoint();
		// const x = this.$canvas.width * drawPoint[0];
		// const y = this.$canvas.height * drawPoint[1];
		// const x = 0;
		// const y = 0;
		this.transform.preMultiplySelf(
			new DOMMatrix()
				// .translateSelf(x, y)
				.rotateSelf(angleDegrees)
				// .translateSelf(-x, -y)
		);
		this.drawHeadshot();
	}

	onCounterRotateClick() {
		this.centeredRotate(-3);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ROTATE_CLICK', 'counterClockwise');
	}

	onClockwiseRotateClick() {
		this.centeredRotate(3);
		Store.emit('TRACK_QUIZPHOTOBOOTH_ROTATE_CLICK', 'clockwise');
	}

	getDrawCoords() {
		const destCoords = { width: this.$canvas.width, height: this.$canvas.height };
		const destRatioLower = destCoords.width / destCoords.height < this.headshotImg.width / this.headshotImg.height;
		// COVER or CONTAIN - TODO: option somewhere instead of hardcoded ?
		const drawMode = 'COVER';

		if (destRatioLower === (drawMode === 'COVER')) {
			destCoords.width = this.headshotImg.width * destCoords.height / this.headshotImg.height;
		} else {
			destCoords.height = this.headshotImg.height * destCoords.width / this.headshotImg.width;
		}

		return {
			from: [0, 0, this.headshotImg.width, this.headshotImg.height],
			to: [
				(this.$canvas.width - destCoords.width) / 2,
				(this.$canvas.height - destCoords.height) / 2,
				destCoords.width,
				destCoords.height
			]
		};
	}

	drawHeadshot() {
		if (this.unmounted) {
			return;
		}
		if (!this.props.showEditScreen || !this.cardCtx) return;
		drawCard(this.props.cardData, this.cardCtx, 'feed');
	}

	closeTouchInstructions = () => {
		this.setState({ showTouchInstructions: false });
	};

	renderMobileInstructions() {
		const copy = this.props?.copy;
		if (this.state.showTouchInstructions) {
			return (
				<div class={s.touchInstructionsWrapper} onClick={this.closeTouchInstructions}>
					<div class={s.touchInstructionsBg} />
					<div class={s.touchInstructions}>
						<div class={s.zoomInstructions}>
							<PinchGesture />
							<span>{copy.$QUIZPHOTOBOOTH_PINCH_TO_ZOOM}</span>
						</div>
						<div class={s.rotateInstructions}>
							<RotateGesture />
							<span>{copy.$QUIZPHOTOBOOTH_ROTATE_FINGERS}</span>
						</div>
					</div>
				</div>
			);
		}
	}

	renderDesktopOverlay() {
		return (
			<div class={s.desktopOverlay} />
		);
	}

	canvasRef(e) {
		if (e) {
			this.$canvas = e;
			this.cardCtx = e.getContext('2d');
		}
	}
	renderPreviewCanvas() {
		return (
			<canvas ref={this.canvasRef} class={s.photoCanvas} {...feedSize} />
		);
	}

	renderBackButton() {
		return (
			<button class={s.backContainer} onClick={this.onRetake}>
				<HTML tag="span" content={this.props?.copy?.$QUIZPHOTOBOOTH_BACK} markdown inline />
			</button>
		);
	}

	render({ showEditScreen, isDesktopDevice, isDesktopWidth }) {
		const copy = this.props?.copy;
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		return (
			<div class={joinClasses(s.container, showEditScreen && s.show)}>
				<HTML class={joinClasses(s.picTip, s.picPosition, fontStyles.quizPhotoboothQuestion)} content={isDesktopDevice ? copy.$QUIZPHOTOBOOTH_CLICK_AND_DRAG_DESKTOP : copy.$QUIZPHOTOBOOTH_CLICK_AND_DRAG_MOBILE} markdown />
				<div class={s.card}>
					{this.renderBackButton()}
					<div
						class={s.photoContainer}
						ref={ref => this.$canvasContainer = ref}
						onPointerDown={this.handlePointerDown}
					>
						{this.renderPreviewCanvas()}
					</div>
					{!isDesktopDevice && this.renderMobileInstructions(fontStyles)}
					{isDesktopDevice && (
						<div class={s.positionBtns}>
							<button class={s.shortBtn} onClick={this.onZoomInClick}>
								<Plus />
							</button>
							<button class={s.shortBtn} onClick={this.onZoomOutClick}>
								<Minus />
							</button>
							<button class={s.shortBtn} onClick={this.onCounterRotateClick}>
								<RotateLeft />
							</button>
							<button class={s.shortBtn} onClick={this.onClockwiseRotateClick}>
								<RotateRight />
							</button>
						</div>
					)}
				</div>
				<div class={s.buttonRow}>
					<button class={s.buttonPrimary} onClick={this.onConfirm}>
						{copy.$QUIZPHOTOBOOTH_CONTINUE}
					</button>
				</div>
			</div>
		);
	}
}
